import { CalendarX2, MessageSquare, Phone, Tag, User } from "lucide-react";
import React, { useState } from "react";
import TagChip from "../Shared/TagChip/TagChip";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "./dropdown-menu";

const MOCK_TAGS = [
	{ key: "test", name: "Test", color: "#61bd4f" },
	{ key: "vip", name: "VIP", color: "#f2d600" },
	{ key: "onboarding", name: "Onboarding", color: "#0079bf" },
];

/**
 * Live example for /component-showcase.
 *
 * Demonstrates the shadcn dropdown-menu primitive laid out exactly like the real
 * consultant contact menu (Shared/ContactContextMenu) — header label, contact
 * group, a settings section with a leading-icon item and a checkbox item that
 * carries a muted expiry subtext. Uses mock data so it renders without networking.
 */
const DropdownMenuExample: React.FC = () => {
	const [priorityEnabled, setPriorityEnabled] = useState(false);
	const [assignedTags, setAssignedTags] = useState<string[]>(["test"]);

	const toggleTag = (key: string) =>
		setAssignedTags((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
		);

	return (
		<div className="flex flex-col gap-6">
			<p className="text-sm text-gray-600">
				The consultant contact menu opens on right-click over any element with a{" "}
				<code className="rounded bg-gray-100 px-1">data-contact</code> attribute. This example wires
				the same structure to a normal trigger so it can be demoed in isolation.
			</p>

			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button className="inline-flex h-9 w-fit items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-accent hover:text-accent-foreground">
						Open contact menu
					</button>
				</DropdownMenuTrigger>

				<DropdownMenuContent align="start" className="w-60">
					<DropdownMenuLabel className="flex items-center justify-between gap-4">
						<span className="truncate">Ralf Anonymous</span>
						<span className="shrink-0 text-xs font-normal text-muted-foreground">#40745</span>
					</DropdownMenuLabel>

					<DropdownMenuSeparator />

					<DropdownMenuItem asChild>
						<a href="#profile">
							<User />
							<span>Visa profil</span>
						</a>
					</DropdownMenuItem>

					<DropdownMenuItem asChild>
						<a href="tel:0705584048">
							<Phone />
							<span>Ring</span>
							<DropdownMenuShortcut className="tracking-normal text-muted-foreground">
								0705584048
							</DropdownMenuShortcut>
						</a>
					</DropdownMenuItem>

					<DropdownMenuItem onSelect={() => alert("Open message modal")}>
						<MessageSquare />
						<span>Skicka meddelande</span>
					</DropdownMenuItem>

					<DropdownMenuSeparator />
					<DropdownMenuLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						Inställningar
					</DropdownMenuLabel>

					<DropdownMenuItem onSelect={() => alert("Open absence modal")}>
						<CalendarX2 />
						<span>Registrera frånvaro</span>
					</DropdownMenuItem>

					<DropdownMenuCheckboxItem
						checked={priorityEnabled}
						onSelect={(event) => {
							event.preventDefault();
							setPriorityEnabled((prev) => !prev);
						}}
					>
						<div className="flex flex-col">
							<span>Prioriterad matchning</span>
							{priorityEnabled && (
								<span className="text-xs text-muted-foreground">Upphör 13 jul 09:00</span>
							)}
						</div>
					</DropdownMenuCheckboxItem>

					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<Tag />
							<span>Taggar</span>
						</DropdownMenuSubTrigger>
						<DropdownMenuSubContent className="w-56">
							{MOCK_TAGS.map((tag) => (
								<DropdownMenuCheckboxItem
									key={tag.key}
									checked={assignedTags.includes(tag.key)}
									onSelect={(event) => {
										event.preventDefault();
										toggleTag(tag.key);
									}}
								>
									<TagChip name={tag.name} color={tag.color} size="sm" />
								</DropdownMenuCheckboxItem>
							))}
						</DropdownMenuSubContent>
					</DropdownMenuSub>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};

export default DropdownMenuExample;
